import React, { Component, ReactNode } from "react";
import { observer } from "mobx-react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";
import {
  EnrolmentsResource,
  OngoingCourse,
} from "../../stores/EnrolmentStore/Types";
import {
  CourseResource,
  CoursesResource,
} from "../../stores/CoursesStore/Types";
import SpiritTeacherQuiz from "../AllCourses/components/SpiritTeacherQuiz";

interface Props {
  className?: string;
  // Someone can have finished courses with none on the go. They are still a
  // returning learner, so they should not be greeted as a beginner.
  hasFinishedCourses?: boolean;
  userIsInUk: boolean;
}

interface State {
  ongoingCourses: EnrolmentsResource;
  featuredCourses: CoursesResource;
  featuredCoursesLoaded: boolean;
  featuredCoursesFailed: boolean;
  loaded: boolean;
  canScrollNext: boolean;
  canScrollPrev: boolean;
  quizOpen: boolean;
}

const SUGGESTION_COUNT = 3;

// Upcoming courses cannot be started yet, so suggesting one is a dead end.
// Samples are left out too: they exist to convert people who have not bought,
// so offering one to a learner already working through the catalogue is
// backwards, and some are stitched together from lessons of paid courses they
// may already have done. /courses still showcases them under Try before you buy.
//
// The endpoint is shared with other screens, so the filtering happens here
// rather than narrowing it for everybody.
const suggestable = (courses: CoursesResource): CoursesResource =>
  courses.filter(
    (course) => course.content_version === "live" && !course.sample,
  );

// Best sellers first, then the rest. Both groups arrive in admin feature-rank
// order and filter preserves it, so the ranking is kept inside each group.
const bestSellersFirst = (courses: CoursesResource): CoursesResource => [
  ...courses.filter((course) => course.best_seller),
  ...courses.filter((course) => !course.best_seller),
];

// The wording has to survive running out of courses to suggest, so it counts
// what is actually left rather than promising three.
const suggestionCopy = (count: number, isReturningLearner: boolean) => {
  if (!isReturningLearner) {
    return {
      heading: "Not sure where to start?",
      sub: "Here are our three most popular courses.",
    };
  }

  if (count === 1) {
    return {
      heading: "One left on the shelf",
      sub: "You've done all but one. Fancy finishing the set?",
    };
  }

  if (count === 2) {
    return {
      heading: "Two you haven't touched",
      sub: "You've worked through nearly the whole lot. These two are still going spare.",
    };
  }

  return {
    heading: "Current bestsellers",
    sub: "Three our learners keep coming back to.",
  };
};

const sortByLastAccessed = (courseA: OngoingCourse, courseB: OngoingCourse) => {
  const dateA = Date.parse(courseA.last_accessed_at || "") || 0;
  const dateB = Date.parse(courseB.last_accessed_at || "") || 0;

  return dateB - dateA;
};

const courseUrl = (enrolment: OngoingCourse) =>
  enrolment.embedded_host_url
    ? enrolment.embedded_host_url
    : `/enrolments/${enrolment.id}`;

const courseImage = (userIsInUk, enrolment: OngoingCourse) => {
  if (userIsInUk && enrolment?.course?.preview_headshot_alt_image_url) {
    return enrolment?.course?.preview_headshot_alt_image_url;
  }

  return enrolment?.course?.preview_headshot_image_url;
};

const courseImageAlt = (enrolment: OngoingCourse) =>
  enrolment.course.preview_masthead_alt_image_url ||
  `${enrolment.course.name} course thumbnail`;

class OngoingCourses extends Component<Props, State> {
  railRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      ongoingCourses: [],
      featuredCourses: [],
      featuredCoursesLoaded: false,
      featuredCoursesFailed: false,
      loaded: false,
      canScrollNext: false,
      canScrollPrev: false,
      quizOpen: false,
    };
  }

  componentDidMount() {
    getRequest("/api/profile/ongoing.json")
      .then((response: AxiosResponse<EnrolmentsResource>) => {
        const sortedCourses = [...response.data].sort(sortByLastAccessed);

        this.setState({ ongoingCourses: sortedCourses, loaded: true }, () => {
          this.updateScrollState();
          this.loadFeaturedCourses();
        });
      })
      .catch(() => {
        this.setState({ loaded: true }, () => this.loadFeaturedCourses());
      });
  }

  // Suggestions are loaded for everyone, not just people with an empty shelf —
  // a learner who has finished one course is the best person to nudge towards
  // the next. The profile endpoint already drops anything they have enrolled in
  // or passed, so we never suggest a course back to them.
  loadFeaturedCourses = () => {
    getRequest<CoursesResource, void>("/api/dashboard_courses/profile.json")
      .then((response: AxiosResponse<CoursesResource>) => {
        this.setState({
          featuredCourses: bestSellersFirst(suggestable(response.data)).slice(
            0,
            SUGGESTION_COUNT,
          ),
          featuredCoursesLoaded: true,
        });
      })
      .catch(() => {
        this.setState({
          featuredCourses: [],
          featuredCoursesLoaded: true,
          featuredCoursesFailed: true,
        });
      });
  };

  openQuiz = () => {
    this.setState({ quizOpen: true });
  };

  closeQuiz = () => {
    this.setState({ quizOpen: false });
  };

  scrollRail = (direction: "next" | "prev") => {
    const rail = this.railRef.current;

    if (!rail) return;

    const firstCard = rail.querySelector<HTMLElement>(".ongoing-courses__card");
    const cardWidth = firstCard ? firstCard.offsetWidth + 20 : 340;

    rail.scrollBy({
      left: direction === "next" ? cardWidth * 2 : cardWidth * -2,
      behavior: "smooth",
    });
  };

  updateScrollState = () => {
    const rail = this.railRef.current;

    if (!rail) return;

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;

    this.setState({
      canScrollPrev: rail.scrollLeft > 0,
      canScrollNext: rail.scrollLeft < maxScrollLeft - 1,
    });
  };

  renderLoader = () => (
    <section className="ongoing-courses__section container">
      <div className="ongoing-courses__top">
        <div>
          <h2 className="section-heading">Your active courses</h2>
          <p className="section-sub">Loading what you touched last</p>
        </div>
      </div>
      <div
        className="profile-section-loader profile-section-loader--carousel"
        aria-live="polite"
      >
        <div className="profile-section-loader__card" />
        <div className="profile-section-loader__card" />
        <div className="profile-section-loader__card" />
      </div>
    </section>
  );

  renderEmptyState = () => (
    <div className="ongoing-courses__empty">
      <h3>Nothing on the go yet.</h3>
      <p>Pick a course below and your progress will live here.</p>
      <a className="btn btn-primary" href="/courses">
        Find a course to start
      </a>
    </div>
  );

  courseImage = (course: CourseResource) => {
    if (this.props.userIsInUk && course.preview_headshot_alt_image_url) {
      return course.preview_headshot_alt_image_url;
    }

    return (
      course.preview_headshot_image_url || course.preview_masthead_image_url
    );
  };

  renderFeaturedCourseCard = (course: CourseResource, index: number) => {
    // Only genuine best sellers carry the badge. It used to fall back to
    // "Most popular" for whichever course happened to be listed first, which
    // read as a second, differently-worded version of the same badge. A course
    // ticked in admin as rebuilt for AI says so first, as on every other tile.
    const badge = course.rebuilt_for_ai
      ? { text: "Rebuilt for AI", className: "rebuilt" }
      : course.best_seller
        ? { text: "Bestseller", className: "bestseller" }
        : course.new_course || course.went_live_in_the_past_month
          ? { text: "New", className: "new" }
          : null;

    return (
      <a
        className="course-card"
        data-name={course.name}
        href={course.course_path || `/courses/${course.slug}`}
        key={course.id}
      >
        <div className="course-cover">
          {this.courseImage(course) && (
            <img
              alt={`${course.name} course cover`}
              height="181"
              loading="lazy"
              src={this.courseImage(course)}
              width="290"
            />
          )}
          {badge && (
            <span className={`badge ${badge.className}`}>{badge.text}</span>
          )}
        </div>
        <div className="course-body">
          <h3 className="course-title">{course.name}</h3>
          <p className="course-desc">{course.preview_mini_description}</p>
          <div className="course-meta">
            <span className="course-rating">{course.rating.toFixed(1)}</span>
            <span className="course-cta">More info →</span>
          </div>
        </div>
      </a>
    );
  };

  renderStartGrid = () => {
    const { featuredCourses } = this.state;

    return (
      <>
        <div className="start-grid">
          {featuredCourses.map(this.renderFeaturedCourseCard)}
        </div>

        <div className="start-browse">
          <a className="btn btn-primary" href="/courses">
            Browse all courses
          </a>
          <button
            aria-haspopup="dialog"
            className="stars-decide"
            id="quizTriggerStars"
            onClick={this.openQuiz}
            type="button"
          >
            Still torn? <span className="sd-em">Let the stars decide</span>
          </button>
        </div>
      </>
    );
  };

  renderCourseCard = (enrolment: OngoingCourse) => {
    const { userIsInUk } = this.props;
    const fullStars = Math.round(enrolment.course.rating);

    return (
      <a
        className="ongoing-courses__card"
        href={courseUrl(enrolment)}
        key={enrolment.id}
      >
        <div className="ongoing-courses__thumb">
          {enrolment.course.best_seller && (
            <span className="ongoing-courses__tag">Bestseller</span>
          )}
          <img
            alt={courseImageAlt(enrolment)}
            height="160"
            loading="lazy"
            src={courseImage(userIsInUk, enrolment)}
            width="290"
          />
        </div>
        <div className="ongoing-courses__body">
          <h3 className="ongoing-courses__title">{enrolment.course.name}</h3>
          <div className="ongoing-courses__progress-row">
            <div
              aria-label={`${enrolment.percentage_completed}% complete`}
              className="ongoing-courses__progress"
            >
              <span style={{ width: `${enrolment.percentage_completed}%` }} />
            </div>
            <div className="ongoing-courses__pct">
              {enrolment.percentage_completed}%
            </div>
          </div>
          <div className="ongoing-courses__foot">
            <div
              aria-label={`Rated ${enrolment.course.rating} out of 5`}
              className="ongoing-courses__stars"
            >
              {"★".repeat(fullStars)}
              {"☆".repeat(5 - fullStars)}{" "}
              <span>{enrolment.course.rating.toFixed(1)}</span>
            </div>
            <span className="ongoing-courses__more">Resume</span>
          </div>
        </div>
      </a>
    );
  };

  renderScrollButton = (
    direction: "next" | "prev",
    isEnabled: boolean,
  ): ReactNode => {
    const isPrevious = direction === "prev";

    return (
      <button
        aria-label={`Scroll active courses ${isPrevious ? "back" : "forward"}`}
        className={`ongoing-courses__nav ongoing-courses__nav--${direction} ${
          isEnabled ? "show" : ""
        }`}
        disabled={!isEnabled}
        onClick={() => this.scrollRail(direction)}
        type="button"
      >
        <span aria-hidden="true">{isPrevious ? "\u2039" : "\u203A"}</span>
      </button>
    );
  };

  renderCarousel = () => {
    const { canScrollNext, canScrollPrev, ongoingCourses } = this.state;

    return (
      <div className="ongoing-courses__carousel">
        {this.renderScrollButton("prev", canScrollPrev)}
        {this.renderScrollButton("next", canScrollNext)}

        <div
          className="ongoing-courses__rail"
          onScroll={this.updateScrollState}
          ref={this.railRef}
          tabIndex={0}
        >
          {ongoingCourses.map(this.renderCourseCard)}
        </div>
      </div>
    );
  };

  renderActiveSection = () => (
    <section
      className="ongoing-courses__section container"
      aria-labelledby="active-courses-heading"
    >
      <div className="ongoing-courses__top">
        <div>
          <h2 className="section-heading" id="active-courses-heading">
            Your active courses
          </h2>
          <p className="section-sub">Sorted by what you touched last</p>
        </div>
      </div>

      {this.renderCarousel()}
    </section>
  );

  renderSuggestionsSection = () => {
    const { featuredCourses, featuredCoursesLoaded, featuredCoursesFailed } =
      this.state;
    const hasOngoingCourses = this.state.ongoingCourses.length > 0;

    if (!featuredCoursesLoaded) {
      return (
        <section className="ongoing-courses__section container">
          <div
            className="profile-section-loader profile-section-loader--carousel"
            aria-live="polite"
          >
            <div className="profile-section-loader__card" />
            <div className="profile-section-loader__card" />
            <div className="profile-section-loader__card" />
          </div>
        </section>
      );
    }

    // Nothing left to suggest means they have taken everything startable, so
    // the section drops away entirely. Their active and passed courses are
    // already listed above and below.
    if (featuredCourses.length === 0) {
      if (!featuredCoursesFailed || hasOngoingCourses) return null;

      // Only when the request failed and there is nothing else on the page.
      return (
        <section className="ongoing-courses__section container">
          {this.renderEmptyState()}
        </section>
      );
    }

    const { heading, sub } = suggestionCopy(
      featuredCourses.length,
      hasOngoingCourses || !!this.props.hasFinishedCourses,
    );

    return (
      <section
        className="ongoing-courses__section container"
        aria-labelledby="suggested-courses-heading"
      >
        <div className="ongoing-courses__top">
          <div>
            <h2 className="section-heading" id="suggested-courses-heading">
              {heading}
            </h2>
            <p className="section-sub">{sub}</p>
          </div>
        </div>

        {this.renderStartGrid()}
      </section>
    );
  };

  renderSection = () => (
    <>
      {this.state.ongoingCourses.length > 0 && this.renderActiveSection()}
      {this.renderSuggestionsSection()}
    </>
  );

  render() {
    const { loaded } = this.state;

    return (
      <>
        <div
          className={`ongoing-courses ${this.props.className || ""}`}
          id="ongoing-courses"
        >
          {!loaded && this.renderLoader()}
          {loaded && this.renderSection()}
        </div>
        <SpiritTeacherQuiz
          isOpen={this.state.quizOpen}
          onOpen={this.openQuiz}
          onClose={this.closeQuiz}
        />
      </>
    );
  }
}
export default observer(OngoingCourses);
